import React, { useRef, useState, useEffect } from "react";
import axios from "axios";

const API_BASE = ""; // same origin (nginx/uvicorn). If you need absolute: "https://fr.stage.basilhealth.us"

export default function App() {
  // --- Register camera ---
  const videoRefReg = useRef(null);
  const [regName, setRegName] = useState("");
  const [regImage, setRegImage] = useState(null);
  const [regMsg, setRegMsg] = useState("");

  // --- Recognize camera (auto every 5s) ---
  const videoRefRec = useRef(null);
  const [recImage, setRecImage] = useState(null);
  const [lastResult, setLastResult] = useState(null);     // server JSON
  const [lastStatus, setLastStatus] = useState("");       // human friendly
  const [lastTime, setLastTime] = useState(null);
  const [autoOn, setAutoOn] = useState(false);

  // internal guard to prevent overlapping network calls
  const isRequestInFlight = useRef(false);
  const intervalId = useRef(null);

  const [error, setError] = useState("");

  // --------------------------
  // Camera helpers
  // --------------------------
  async function startCamera(ref) {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (ref.current) ref.current.srcObject = stream;
    } catch (e) {
      setError(
        "Camera blocked by browser. On Chrome you need HTTPS (or enable the flag at chrome://flags/#unsafely-treat-insecure-origin-as-secure for your IP)."
      );
    }
  }

  function stopCamera(ref) {
    const s = ref.current?.srcObject;
    if (s) s.getTracks().forEach((t) => t.stop());
    if (ref.current) ref.current.srcObject = null;
  }

  function captureToDataURL(ref) {
    const video = ref.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg"); // data URL
  }

  // --------------------------
  // Register flow
  // --------------------------
  async function handleRegister() {
    setRegMsg("");
    setError("");
    if (!regName.trim()) {
      setError("Please enter a name before registering.");
      return;
    }
    const dataUrl = captureToDataURL(videoRefReg);
    if (!dataUrl) {
      setError("Could not capture image from camera.");
      return;
    }
    setRegImage(dataUrl);

    // convert data URL -> File and send as multipart/form-data
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], `${regName}.jpg`, { type: "image/jpeg" });

    const form = new FormData();
    form.append("name", regName.trim());
    form.append("file", file);

    try {
      const r = await axios.post(`${API_BASE}/api/register`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRegMsg(JSON.stringify(r.data, null, 2));
    } catch (e) {
      setError(e?.response?.data || e.message);
    }
  }

  // --------------------------
  // Manual recognize (one-shot)
  // --------------------------
  async function handleRecognizeOnce() {
    setError("");
    const dataUrl = captureToDataURL(videoRefRec);
    if (!dataUrl) {
      setError("Could not capture image from camera.");
      return;
    }
    setRecImage(dataUrl);
    await doRecognize(dataUrl);
  }

  async function doRecognize(dataUrl) {
    if (isRequestInFlight.current) return; // prevent overlap
    isRequestInFlight.current = true;
    try {
      const r = await axios.post(`${API_BASE}/api/recognize`, { image: dataUrl });
      setLastResult(r.data);
      setLastTime(new Date().toLocaleTimeString());
      // Build a simple status
      if (r.data?.ok) {
        const faces = r.data.faces || [];
        if (faces.length === 0) {
          setLastStatus("No face detected");
        } else {
          const names = faces.map(f => f.name || "Unknown");
          // If *any* is recognized (not "Unknown"), mark recognized
          const recognized = names.some(n => n && n !== "Unknown");
          setLastStatus(recognized ? `Recognized: ${names.join(", ")}` : "Not recognized");
        }
      } else {
        setLastStatus(`Error: ${r.data?.error || "Unknown error"}`);
      }
    } catch (e) {
      setLastStatus(`Error: ${e?.response?.data || e.message}`);
    } finally {
      isRequestInFlight.current = false;
    }
  }

  // --------------------------
  // Auto-recognize loop (every 5s)
  // --------------------------
  useEffect(() => {
    if (!autoOn) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      return;
    }

    // fire immediately once
    const first = captureToDataURL(videoRefRec);
    if (first) {
      setRecImage(first);
      void doRecognize(first);
    }

    intervalId.current = setInterval(() => {
      const dataUrl = captureToDataURL(videoRefRec);
      if (!dataUrl) return;
      setRecImage(dataUrl);
      void doRecognize(dataUrl);
    }, 5000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [autoOn]);

  // --------------------------
  // Mount / unmount
  // --------------------------
  useEffect(() => {
    // start both cameras by default
    startCamera(videoRefReg);
    startCamera(videoRefRec);
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
      stopCamera(videoRefReg);
      stopCamera(videoRefRec);
    };
  }, []);

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial", padding: 16, maxWidth: 980, margin: "0 auto" }}>
      <h1>Face Recognition App</h1>

      {/* REGISTER */}
      <section style={{ border: "1px solid #ddd", padding: 12, marginBottom: 16, borderRadius: 8 }}>
        <h2>Register a face</h2>
        <div style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => startCamera(videoRefReg)}>Start Camera</button>
          <input
            placeholder="Name to register"
            value={regName}
            onChange={(e) => setRegName(e.target.value)}
            style={{ padding: 8, flex: 1 }}
          />
          <button onClick={handleRegister}>Capture & Register</button>
          <button onClick={() => stopCamera(videoRefReg)}>Stop Camera</button>
        </div>
        <video ref={videoRefReg} autoPlay playsInline width="480" height="360" style={{ background: "#000" }} />
        {regImage && (
          <div style={{ marginTop: 8 }}>
            <strong>Captured (register):</strong>
            <div><img src={regImage} alt="register capture" width="240" /></div>
          </div>
        )}
        {regMsg && <pre style={{ whiteSpace: "pre-wrap", background: "#f6f6f6", padding: 8, borderRadius: 6 }}>Register response: {regMsg}</pre>}
      </section>

      {/* RECOGNIZE */}
      <section style={{ border: "1px solid #ddd", padding: 12, borderRadius: 8 }}>
        <h2>Recognize</h2>
        <div style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => startCamera(videoRefRec)}>Start Camera</button>
          <button onClick={handleRecognizeOnce}>Capture & Recognize (one-shot)</button>
          <button
            onClick={() => setAutoOn((v) => !v)}
            style={{ background: autoOn ? "#0a0" : undefined, color: autoOn ? "#fff" : undefined }}
          >
            {autoOn ? "Stop Auto Recognize" : "Start Auto Recognize (5s)"}
          </button>
          <button onClick={() => stopCamera(videoRefRec)}>Stop Camera</button>
        </div>

        <video ref={videoRefRec} autoPlay playsInline width="480" height="360" style={{ background: "#000" }} />
        {recImage && (
          <div style={{ marginTop: 8 }}>
            <strong>Last captured:</strong>
            <div><img src={recImage} alt="recognize capture" width="240" /></div>
          </div>
        )}

        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#f2f2f2" }}>
          <strong>Status:</strong>{" "}
          {lastStatus ? lastStatus : "No recognition attempt yet."}
          {lastTime && <span style={{ marginLeft: 10, color: "#666" }}>(at {lastTime})</span>}
        </div>

        {lastResult && (
          <details style={{ marginTop: 8 }}>
            <summary>Raw response</summary>
            <pre style={{ whiteSpace: "pre-wrap", background: "#f6f6f6", padding: 8, borderRadius: 6 }}>
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </details>
        )}
      </section>

      {error && (
        <p style={{ color: "crimson", marginTop: 16 }}>
          <strong>Error: </strong>
          {String(error)}
        </p>
      )}

      <hr />
      <p style={{ fontSize: 14, color: "#666" }}>
        Tip (Chrome): camera requires a secure context (HTTPS or localhost). For quick LAN testing, you can enable the flag at{" "}
        <code>chrome://flags/#unsafely-treat-insecure-origin-as-secure</code> and add your origin (e.g. <code>http://192.168.1.236:8044</code>),
        then allow camera permissions. Prefer HTTPS in production.
      </p>
    </div>
  );
}

